import { useCallback, useState } from "react";

interface FormDifferenceViewParams {
    currentFormDefinition: PolicyFormDefinition;
    originalFormDefinition: PolicyFormDefinition;
}

interface FormDifferenceResult {
    differences: FormDiff[];
    hierarchicalDifference: HierarchicalFormDiff[];
    copied: boolean;
    handleCopyFormDifference: () => void;
}

export function useFormDifferenceView({
    currentFormDefinition,
    originalFormDefinition
}: FormDifferenceViewParams): FormDifferenceResult {

    const [copied, setCopied] = useState(false);

    const normalizeForm = (formDefinition: PolicyFormDefinition): NormalizedFormDefinition => {
        return {
            sections: formDefinition.sections.map(section => ({
                ...section,
                subsections: section.subsections.map(subsection => ({
                    ...subsection,
                    questionGroups: subsection.questionGroups.map(group => ({
                        ...group,
                        questions: group.questions.map(
                            (questionRef) => {
                                const question = formDefinition.questions[questionRef];
                                if (!question) {
                                    console.warn(`Question reference "${questionRef}" not found in formDefinition.questions`);
                                }
                                return question;
                            }
                        ).filter(Boolean),
                    })),
                })),
            }))
        }
    };

    const compareSections = (
        originalSections: NormalizedPolicyFormSection[],
        currentSections: NormalizedPolicyFormSection[],
        differences: FormDiff[],
        createDiff: Function
    ) => {
        const originalMap = new Map(originalSections.map(s => [s.id, s]));
        const currentMap = new Map(currentSections.map(s => [s.id, s]));

        // Find added sections
        for (const [id, section] of currentMap) {
            if (!originalMap.has(id)) {
                differences.push(createDiff(
                    'ADD',
                    'section',
                    `sections.${id}`,
                    `Added section "${section.title}"`,
                    {
                        sectionId: section.id,
                        sectionTitle: section.title,
                        newValue: section
                    }
                ));
            }
        }

        // Find removed sections
        for (const [id, section] of originalMap) {
            if (!currentMap.has(id)) {
                differences.push(createDiff(
                    'REMOVE',
                    'section',
                    `sections.${id}`,
                    `Removed section "${section.title}"`,
                    {
                        sectionId: section.id,
                        sectionTitle: section.title,
                        oldValue: section
                    }
                ));
            }
        }

        // Find changed sections
        for (const [id, currentSection] of currentMap) {
            const originalSection = originalMap.get(id);
            if (originalSection) {
                // Compare section properties
                compareProperties(
                    originalSection,
                    currentSection,
                    `sections.${id}`,
                    differences,
                    createDiff,
                    'section',
                    {
                        sectionId: id,
                        sectionTitle: currentSection.title
                    },
                    ['id', 'subsections'] // Skip these properties
                );

                // Compare subsections
                compareSubsections(
                    originalSection.subsections || [],
                    currentSection.subsections || [],
                    `sections.${id}.subsections`,
                    differences,
                    createDiff,
                    {
                        sectionId: id,
                        sectionTitle: currentSection.title
                    }
                );
            }
        }
    }

    const compareSubsections = (
        originalSubsections: NormalizedPolicyFormSubSection[],
        currentSubsections: NormalizedPolicyFormSubSection[],
        basePath: string,
        differences: FormDiff[],
        createDiff: Function,
        parentContext: { sectionId: string; sectionTitle: string }
    ) => {
        const originalMap = new Map(originalSubsections.map(s => [s.id, s]));
        const currentMap = new Map(currentSubsections.map(s => [s.id, s]));

        // Find added subsections
        for (const [id, subsection] of currentMap) {
            if (!originalMap.has(id)) {
                differences.push(createDiff(
                    'ADD',
                    'subsection',
                    `${basePath}.${id}`,
                    `Added subsection "${subsection.title}" to section "${parentContext.sectionTitle}"`,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: subsection.id,
                        subsectionTitle: subsection.title,
                        newValue: subsection
                    }
                ));
            }
        }

        // Find removed subsections
        for (const [id, subsection] of originalMap) {
            if (!currentMap.has(id)) {
                differences.push(createDiff(
                    'REMOVE',
                    'subsection',
                    `${basePath}.${id}`,
                    `Removed subsection "${subsection.title}" from section "${parentContext.sectionTitle}"`,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: subsection.id,
                        subsectionTitle: subsection.title,
                        oldValue: subsection
                    }
                ));
            }
        }

        // Find changed subsections
        for (const [id, currentSubsection] of currentMap) {
            const originalSubsection = originalMap.get(id);
            if (originalSubsection) {
                // Compare subsection properties
                compareProperties(
                    originalSubsection,
                    currentSubsection,
                    `${basePath}.${id}`,
                    differences,
                    createDiff,
                    'subsection',
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: id,
                        subsectionTitle: currentSubsection.title
                    },
                    ['id', 'questionGroups']
                );

                // Compare question groups
                compareQuestionGroups(
                    originalSubsection.questionGroups || [],
                    currentSubsection.questionGroups || [],
                    `${basePath}.${id}.questionGroups`,
                    differences,
                    createDiff,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: id,
                        subsectionTitle: currentSubsection.title
                    }
                );
            }
        }
    }

    const compareQuestionGroups = (
        originalGroups: NormalizedPolicyFormQuestionGroup[],
        currentGroups: NormalizedPolicyFormQuestionGroup[],
        basePath: string,
        differences: FormDiff[],
        createDiff: Function,
        parentContext: { sectionId: string; sectionTitle: string; subsectionId: string; subsectionTitle: string }
    ) => {
        const originalMap = new Map(originalGroups.map(g => [g.id, g]));
        const currentMap = new Map(currentGroups.map(g => [g.id, g]));

        // Find added groups
        for (const [id, group] of currentMap) {
            if (!originalMap.has(id)) {
                differences.push(createDiff(
                    'ADD',
                    'questionGroup',
                    `${basePath}.${id}`,
                    `Added question group "${group.title || 'Untitled'}" to subsection "${parentContext.subsectionTitle}"`,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: group.id,
                        questionGroupTitle: group.title,
                        newValue: group
                    }
                ));
            }
        }

        // Find removed groups
        for (const [id, group] of originalMap) {
            if (!currentMap.has(id)) {
                differences.push(createDiff(
                    'REMOVE',
                    'questionGroup',
                    `${basePath}.${id}`,
                    `Removed question group "${group.title || 'Untitled'}" from subsection "${parentContext.subsectionTitle}"`,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: group.id,
                        questionGroupTitle: group.title,
                        oldValue: group
                    }
                ));
            }
        }

        // Find changed groups
        for (const [id, currentGroup] of currentMap) {
            const originalGroup = originalMap.get(id);
            if (originalGroup) {
                // Compare group properties
                compareProperties(
                    originalGroup,
                    currentGroup,
                    `${basePath}.${id}`,
                    differences,
                    createDiff,
                    'questionGroup',
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: id,
                        questionGroupTitle: currentGroup.title
                    },
                    ['id', 'questions']
                );

                // Compare questions
                compareQuestions(
                    originalGroup.questions || [],
                    currentGroup.questions || [],
                    `${basePath}.${id}.questions`,
                    differences,
                    createDiff,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: id,
                        questionGroupTitle: currentGroup.title || 'Untitled Question Group'
                    }
                );
            }
        }
    }

    const compareQuestions = (
        originalQuestions: PolicyFormQuestion[],
        currentQuestions: PolicyFormQuestion[],
        basePath: string,
        differences: FormDiff[],
        createDiff: Function,
        parentContext: {
            sectionId: string;
            sectionTitle: string;
            subsectionId: string;
            subsectionTitle: string;
            questionGroupId: string;
            questionGroupTitle: string;
        }
    ) => {
        const originalMap = new Map(originalQuestions.map(q => [q?.questionRef, q]));
        const currentMap = new Map(currentQuestions.map(q => [q?.questionRef, q]));

        for (const [id, question] of currentMap) {
            if (!originalMap.has(id)) {
                differences.push(createDiff(
                    'ADD',
                    'question',
                    `${basePath}.${id}`,
                    `Added question "${question.title}" to question group "${parentContext.questionGroupTitle}"`,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: parentContext.questionGroupId,
                        questionGroupTitle: parentContext.questionGroupTitle,
                        questionId: question.id,
                        questionTitle: question.title,
                        newValue: question
                    }
                ));
            }
        }

        // Find removed questions
        for (const [id, question] of originalMap) {
            if (!currentMap.has(id)) {
                differences.push(createDiff(
                    'REMOVE',
                    'question',
                    `${basePath}.${id}`,
                    `Removed question "${question.title}" from question group "${parentContext.questionGroupTitle}"`,
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: parentContext.questionGroupId,
                        questionGroupTitle: parentContext.questionGroupTitle,
                        questionId: question.id,
                        questionTitle: question.title,
                        oldValue: question
                    }
                ));
            }
        }

        // Find changed questions
        for (const [id, currentQuestion] of currentMap) {
            const originalQuestion = originalMap.get(id);
            if (originalQuestion) {
                // Compare question properties
                compareProperties(
                    originalQuestion,
                    currentQuestion,
                    `${basePath}.${id}`,
                    differences,
                    createDiff,
                    'question',
                    {
                        sectionId: parentContext.sectionId,
                        sectionTitle: parentContext.sectionTitle,
                        subsectionId: parentContext.subsectionId,
                        subsectionTitle: parentContext.subsectionTitle,
                        questionGroupId: parentContext.questionGroupId,
                        questionGroupTitle: parentContext.questionGroupTitle,
                        questionId: id,
                        questionTitle: currentQuestion.title
                    },
                    ['id']
                );
            }
        }
    }

    const compareProperties = (
        original: any,
        current: any,
        basePath: string,
        differences: FormDiff[],
        createDiff: Function,
        level: FormDiff['level'],
        context: any,
        skipProperties: string[] = []
    ) => {
        const allKeys = new Set([...Object.keys(original || {}), ...Object.keys(current || {})]);

        for (const key of allKeys) {
            if (skipProperties.includes(key)) continue;

            const originalValue = original?.[key];
            const currentValue = current?.[key];

            if (!deepEqual(originalValue, currentValue)) {
                let description = '';
                const itemTitle = context.questionTitle || context.questionGroupTitle || context.subsectionTitle || context.sectionTitle || 'Unknown';

                if (originalValue === undefined) {
                    description = `Added ${key} "${typeof(currentValue) == "string" ? currentValue : JSON.stringify(currentValue)}" to ${level} "${itemTitle}"`;
                } else if (currentValue === undefined) {
                    description = `Removed ${key} "${typeof(originalValue) == "string" ? currentValue : JSON.stringify(originalValue)}" from ${level} "${itemTitle}"`;
                } else {
                    description = `Changed ${key} of ${level} "${itemTitle}" from "${typeof(originalValue) == "string" ? originalValue : JSON.stringify(originalValue)} " to "${typeof(currentValue) == "string" ? currentValue : JSON.stringify(currentValue)}"`;
                }

                differences.push(createDiff(
                    'CHANGE',
                    'property',
                    `${basePath}.${key}`,
                    description,
                    {
                        ...context,
                        propertyName: key,
                        oldValue: originalValue,
                        newValue: currentValue
                    }
                ));
            }
        }
    }

    const deepEqual = (a: any, b: any): boolean => {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (typeof a !== typeof b) return false;

        if (typeof a === 'object') {
            if (Array.isArray(a) !== Array.isArray(b)) return false;

            if (Array.isArray(a)) {
                if (a.length !== b.length) return false;
                return a.every((item, index) => deepEqual(item, b[index]));
            }

            const keysA = Object.keys(a);
            const keysB = Object.keys(b);
            if (keysA.length !== keysB.length) return false;

            return keysA.every(key => deepEqual(a[key], b[key]));
        }

        return false;
    }

    const compareFormDefinitions = (
        originalForm: NormalizedFormDefinition,
        currentForm: NormalizedFormDefinition
    ): FormDiff[] => {
        const differences: FormDiff[] = [];
        let diffCounter = 0;

        const createDiff = (
            type: 'ADD' | 'REMOVE' | 'CHANGE',
            level: FormDiff['level'],
            path: string,
            description: string,
            context: {
                sectionId?: string;
                sectionTitle?: string;
                subsectionId?: string;
                subsectionTitle?: string;
                questionGroupId?: string;
                questionGroupTitle?: string;
                questionId?: string;
                questionTitle?: string;
                propertyName?: string;
                newValue?: any;
                oldValue?: any;
            } = {}
        ): FormDiff => {
            diffCounter++;

            // Determine severity based on level and type
            let severity: 'low' | 'medium' | 'high' = 'medium';
            if (level === 'section' || (level === 'subsection' && type !== 'CHANGE')) {
                severity = 'high';
            } else if (level === 'property' && context.propertyName && ['title', 'placeholder', 'description'].includes(context.propertyName)) {
                severity = 'low';
            }

            // Determine category
            let category: FormDiff['category'] = 'content';
            if (level === 'section' || level === 'subsection' || level === 'questionGroup' || (level === 'question' && type !== 'CHANGE')) {
                category = 'structure';
            } else if (context.propertyName && ['required', 'disabled', 'hidden', 'conditional'].includes(context.propertyName)) {
                category = 'behavior';
            } else if (context.propertyName && ['validationType', 'minRows', 'maxRows', 'type'].includes(context.propertyName)) {
                category = 'validation';
            }

            return {
                id: `diff-${diffCounter}`,
                type,
                level,
                path,
                description,
                severity,
                category,
                ...context
            };
        };

        // Compare sections
        compareSections(originalForm.sections || [], currentForm.sections || [], differences, createDiff);

        return differences;
    }

    const getDiffStatistics = (differences: FormDiff[]) => {
        return {
            total: differences.length,
            additions: differences.filter(d => d.type === 'ADD').length,
            removals: differences.filter(d => d.type === 'REMOVE').length,
            changes: differences.filter(d => d.type === 'CHANGE').length,
            byCategory: {
                structure: differences.filter(d => d.category === 'structure').length,
                content: differences.filter(d => d.category === 'content').length,
                validation: differences.filter(d => d.category === 'validation').length,
                behavior: differences.filter(d => d.category === 'behavior').length,
            },
            bySeverity: {
                high: differences.filter(d => d.severity === 'high').length,
                medium: differences.filter(d => d.severity === 'medium').length,
                low: differences.filter(d => d.severity === 'low').length,
            }
        };
    }

    const generateHierarchyDescription = (node: HierarchicalFormDiff): string => {
        const { additions, removals, modifications } = node.changesSummary;
        const parts = [];

        if (additions > 0) parts.push(`${additions} addition${additions > 1 ? 's' : ''}`);
        if (removals > 0) parts.push(`${removals} removal${removals > 1 ? 's' : ''}`);
        if (modifications > 0) parts.push(`${modifications} modification${modifications > 1 ? 's' : ''}`);

        const changesText = parts.join(', ');

        switch (node.level) {
            case 'section':
                return `Section "${node.sectionTitle}" has ${changesText}`;
            case 'subsection':
                return `Subsection "${node.subsectionTitle}" has ${changesText}`;
            case 'questionGroup':
                return `Question Group "${node.questionGroupTitle}" has ${changesText}`;
            default:
                return `${changesText}`;
        }
    }

    const groupChangesHierarchically = (changes: FormDiff[]): HierarchicalFormDiff[] => {
        const hierarchyMap = new Map<string, HierarchicalFormDiff>();

        // Process each change and organize into hierarchy
        changes.forEach(change => {
            const keys = [];

            // Build hierarchy keys based on the change context
            if (change.sectionId) {
                const sectionKey = `section-${change.sectionId}`;
                keys.push({
                    key: sectionKey,
                    level: 'section' as const,
                    sectionId: change.sectionId,
                    sectionTitle: change.sectionTitle || 'Untitled Section'
                });

                if (change.subsectionId) {
                    const subsectionKey = `${sectionKey}-subsection-${change.subsectionId}`;
                    keys.push({
                        key: subsectionKey,
                        level: 'subsection' as const,
                        sectionId: change.sectionId,
                        sectionTitle: change.sectionTitle || 'Untitled Section',
                        subsectionId: change.subsectionId,
                        subsectionTitle: change.subsectionTitle || 'Untitled Subsection'
                    });

                    if (change.questionGroupId) {
                        const groupKey = `${subsectionKey}-group-${change.questionGroupId}`;
                        keys.push({
                            key: groupKey,
                            level: 'questionGroup' as const,
                            sectionId: change.sectionId,
                            sectionTitle: change.sectionTitle || 'Untitled Section',
                            subsectionId: change.subsectionId,
                            subsectionTitle: change.subsectionTitle || 'Untitled Subsection',
                            questionGroupId: change.questionGroupId,
                            questionGroupTitle: change.questionGroupTitle || 'Untitled Question Group'
                        });
                    }
                }
            }

            // Create or update hierarchy nodes
            keys.forEach(keyInfo => {
                if (!hierarchyMap.has(keyInfo.key)) {
                    hierarchyMap.set(keyInfo.key, {
                        id: keyInfo.key,
                        level: keyInfo.level,
                        sectionId: keyInfo.sectionId,
                        sectionTitle: keyInfo.sectionTitle,
                        subsectionId: keyInfo.subsectionId,
                        subsectionTitle: keyInfo.subsectionTitle,
                        questionGroupId: keyInfo.questionGroupId,
                        questionGroupTitle: keyInfo.questionGroupTitle,
                        changes: [],
                        totalChanges: 0,
                        changesSummary: { additions: 0, removals: 0, modifications: 0 },
                        highestSeverity: 'low',
                        categories: [],
                        description: '',
                        children: []
                    });
                }

                const node = hierarchyMap.get(keyInfo.key)!;
                node.changes.push(change);
                node.totalChanges++;

                // Update summary
                if (change.type === 'ADD') node.changesSummary.additions++;
                else if (change.type === 'REMOVE') node.changesSummary.removals++;
                else node.changesSummary.modifications++;

                // Update highest severity
                const severityOrder = { low: 0, medium: 1, high: 2 };
                if (severityOrder[change.severity] > severityOrder[node.highestSeverity]) {
                    node.highestSeverity = change.severity;
                }

                // Update categories
                if (!node.categories.includes(change.category)) {
                    node.categories.push(change.category);
                }
            });
        });

        // Build hierarchy relationships and generate descriptions
        const hierarchyArray = Array.from(hierarchyMap.values());
        const rootNodes: HierarchicalFormDiff[] = [];

        hierarchyArray.forEach(node => {
            // Generate description
            node.description = generateHierarchyDescription(node);

            if (node.level === 'section') {
                rootNodes.push(node);
            }
        });

        // Build parent-child relationships
        rootNodes.forEach(section => {
            section.children = hierarchyArray.filter(node =>
                node.level === 'subsection' && node.sectionId === section.sectionId
            );

            section.children.forEach(subsection => {
                subsection.children = hierarchyArray.filter(node =>
                    node.level === 'questionGroup' &&
                    node.sectionId === subsection.sectionId &&
                    node.subsectionId === subsection.subsectionId
                );
            });
        });

        return rootNodes.sort((a, b) => b.totalChanges - a.totalChanges);
    }

    const differences = compareFormDefinitions(normalizeForm(originalFormDefinition), normalizeForm(currentFormDefinition));

    const handleCopyFormDifference = useCallback(() => {
        try {
            navigator.clipboard.writeText(JSON.stringify(differences, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            console.error("Failed to copy")
        }
    }, [
        differences
    ]);
    return {
        differences,
        hierarchicalDifference: groupChangesHierarchically(differences),
        copied,
        handleCopyFormDifference
    }
}